import React from 'react';
import { useDispatch } from 'react-redux';
import { Box, Checkbox, FormControlLabel, Paper, Typography, CircularProgress } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { useQuery, useMutation } from '@apollo/client';
import i18next from 'i18next';

import { dispatchException } from 'helper/snackbar';
import {
  LIST_AVAILABLE_HOTEL_STANDARD_DOCUMENTS,
  LIST_SELECTED_OFFER_STANDARD_DOCUMENTS,
  SET_OFFER_STANDARD_DOCUMENT,
  LIST_OFFER_DOCUMENTS,
} from '../pages/offers/gql';

const MAX_TOTAL_SIZE = 10 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

interface OfferStandardDocumentsProps {
  offerId: number;
  canEdit: boolean;
}

export const OfferStandardDocuments: React.FC<OfferStandardDocumentsProps> = ({ offerId, canEdit }) => {
  const dispatch = useDispatch();

  const { data: availableData, loading: availableLoading } = useQuery(LIST_AVAILABLE_HOTEL_STANDARD_DOCUMENTS, {
    variables: { offerId },
    fetchPolicy: 'network-only',
  });

  const {
    data: selectedData,
    loading: selectedLoading,
    refetch: refetchSelected,
  } = useQuery(LIST_SELECTED_OFFER_STANDARD_DOCUMENTS, { variables: { offerId }, fetchPolicy: 'network-only' });

  const { data: adHocData } = useQuery(LIST_OFFER_DOCUMENTS, { variables: { offerId }, fetchPolicy: 'network-only' });

  const [setStandardDocument, { loading: mutating }] = useMutation(SET_OFFER_STANDARD_DOCUMENT, {
    onCompleted: () => refetchSelected(),
    onError: err => {
      if (err.graphQLErrors?.some(e => e.extensions?.code === 'ATTACHMENT_SIZE_EXCEEDED')) {
        dispatchException(dispatch, new Error(String(i18next.t('offer-standard-document-size-exceeded'))));
      } else {
        dispatchException(dispatch, err);
      }
    },
  });

  if (availableLoading || selectedLoading) return <CircularProgress size={20} />;

  const available = availableData?.availableHotelStandardDocuments || [];
  const selectedIds = new Set((selectedData?.offerStandardDocuments || []).map((d: any) => d.id));

  if (available.length === 0) return null;

  const selectedTotalSize = available
    .filter((d: any) => selectedIds.has(d.id))
    .reduce((sum: number, d: any) => sum + (d.documentFile?.fileSize || 0), 0);

  const adHocTotalSize = (adHocData?.offerDocuments || []).reduce((sum: number, d: any) => sum + (d.fileSize || 0), 0);

  const handleToggle = (docId: number, docSize: number, currentlySelected: boolean) => {
    if (!canEdit) return;

    if (!currentlySelected) {
      const newTotal = selectedTotalSize + docSize + adHocTotalSize;
      if (newTotal > MAX_TOTAL_SIZE) {
        dispatchException(dispatch, new Error(String(i18next.t('offer-standard-document-size-exceeded'))));
        return;
      }
    }

    setStandardDocument({
      variables: { offerId, hotelDocumentId: docId, selected: !currentlySelected },
    });
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={2}>
        <AttachFileIcon sx={{ mr: 1 }} />
        <Typography variant="h6">{i18next.t('offer-standard-documents-title')}</Typography>
      </Box>

      {available.map((doc: any) => {
        const isSelected = selectedIds.has(doc.id);
        const label = doc.displayName || doc.documentFile?.originalFilename || `Dokument ${doc.id}`;
        const size = doc.documentFile?.fileSize || 0;

        return (
          <Box key={doc.id} display="flex" alignItems="center">
            <FormControlLabel
              control={<Checkbox checked={isSelected} disabled={!canEdit || mutating} onChange={() => handleToggle(doc.id, size, isSelected)} />}
              label={
                <Typography variant="body2">
                  {label}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                    ({formatFileSize(size)})
                  </Typography>
                </Typography>
              }
            />
          </Box>
        );
      })}

      {canEdit && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {i18next.t('offer-document-upload-hint')}
        </Typography>
      )}
    </Paper>
  );
};
